import { useEffect, useState, useCallback } from "react";
import { agentsDB, marketplaceDB, type DBAgent } from "@/lib/db";
import { useRealtime } from "@/hooks/useRealtime";
import { MODELS } from "@/lib/models";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Wrench, Save, Sparkles, Check, ShoppingBag, Trash2, Edit2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/Skeleton";
import { notifyError } from "@/lib/errorToast";

export function BuilderStudio() {
  const [agents, setAgents] = useState<DBAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [models, setModels] = useState<string[]>(["gemini-3-flash"]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await agentsDB.list();
      setAgents(data);
    } catch (e) {
      notifyError(e as Error, "Falha ao carregar agentes");
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useRealtime("agents", refresh);

  function toggle(id: string) {
    setModels((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }

  function reset() {
    setEditingId(null);
    setName(""); setRole(""); setSystemPrompt(""); setModels(["gemini-3-flash"]);
  }

  function loadAgent(a: DBAgent) {
    setEditingId(a.id);
    setName(a.name);
    setRole(a.role ?? "");
    setSystemPrompt(a.system_prompt ?? "");
    setModels(a.models?.length ? a.models : ["gemini-3-flash"]);
  }

  async function save() {
    if (!name) return toast.error("Agent precisa de nome");
    if (models.length === 0) return toast.error("Pick at least one model");
    setBusy(true);
    try {
      if (editingId) {
        await agentsDB.update(editingId, { name, role, system_prompt: systemPrompt, models } as Partial<DBAgent>);
        toast.success(`Agent "${name}" atualizado`);
      } else {
        await agentsDB.create({ name, role, system_prompt: systemPrompt, models });
        toast.success(`Agent "${name}" criado`);
      }
      reset();
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha ao salvar");
    }
    setBusy(false);
  }

  async function remove(id: string, agentName: string) {
    if (!confirm(`Excluir agente "${agentName}"?`)) return;
    try {
      await agentsDB.remove(id);
      toast.success("Agent removido");
      if (editingId === id) reset();
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha ao excluir");
    }
  }

  async function publish() {
    if (!name) return toast.error("Agent precisa de nome");
    setBusy(true);
    try {
      await marketplaceDB.publish({
        kind: "agent",
        name,
        description: role || systemPrompt.slice(0, 120),
        payload: { name, role, system_prompt: systemPrompt, models },
      });
      toast.success(`"${name}" publicado no Marketplace`);
    } catch (e) {
      notifyError(e as Error, "Falha ao publicar");
    }
    setBusy(false);
  }

  return (
    <div className="h-full overflow-auto p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-primary" /> Builder Studio
          </h2>
          <p className="text-sm text-muted-foreground">
            {editingId ? "Editando agente existente" : "Compose um novo agent — name, role, prompt, models"}
          </p>
        </div>
        {editingId && (
          <Button size="sm" variant="outline" onClick={reset} aria-label="Novo agente" className="gap-1">
            <Plus className="w-3.5 h-3.5" /> Novo
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <Label>Agent name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Research Analyst" aria-label="Nome do agente" />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. researcher, coder, planner" aria-label="Role" />
          </div>
          <div>
            <Label>System prompt</Label>
            <Textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="You are a precise, helpful agent that..."
              rows={8}
              aria-label="System prompt"
            />
          </div>
          <Button onClick={save} disabled={busy} className="w-full gap-2" aria-label={editingId ? "Atualizar agente" : "Criar agente"}>
            <Save className="w-4 h-4" /> {busy ? "Saving..." : editingId ? "Update agent" : "Create agent"}
          </Button>
          <Button onClick={publish} disabled={busy} variant="outline" className="w-full gap-2" aria-label="Publicar no Marketplace">
            <ShoppingBag className="w-4 h-4" /> Publish to Marketplace
          </Button>
        </div>

        <div className="glass-panel p-4 rounded-lg border border-primary/20">
          <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3">
            <Sparkles className="w-4 h-4 text-accent" /> Models ({models.length} selected)
          </h3>
          <div className="space-y-1 max-h-[400px] overflow-auto pr-2">
            {MODELS.map((m) => {
              const on = models.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggle(m.id)}
                  aria-label={`Toggle model ${m.label}`}
                  aria-pressed={on}
                  className={`w-full text-left flex items-center gap-2 p-2 rounded text-xs hover:bg-surface-2 ${on ? "bg-primary/10 border border-primary/30" : "border border-transparent"}`}
                >
                  <div className={`w-4 h-4 rounded border ${on ? "bg-primary border-primary" : "border-border"} grid place-items-center`}>
                    {on && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-mono">{m.label}</div>
                    <div className="text-[10px] text-muted-foreground">{m.vendor}</div>
                  </div>
                  <span className="text-[9px] font-mono text-muted-foreground">{m.family}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* My agents — DB-backed CRUD */}
      <div className="glass-panel rounded-lg border border-primary/20">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Wrench className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold flex-1">My agents ({agents.length})</h3>
          <span className="text-[10px] font-mono text-success">● live</span>
        </div>
        <div className="p-3">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
            </div>
          ) : agents.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">Nenhum agente ainda — crie acima.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {agents.map((a) => (
                <div key={a.id} className={`p-3 rounded border ${editingId === a.id ? "border-primary bg-primary/5" : "border-border"} flex flex-col gap-2`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-sm truncate">{a.name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">{a.role ?? "—"}</div>
                    </div>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30 shrink-0">
                      {a.models?.length ?? 0} model{(a.models?.length ?? 0) !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => loadAgent(a)} aria-label={`Editar ${a.name}`} className="h-7 flex-1 gap-1 text-[11px]">
                      <Edit2 className="w-3 h-3" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(a.id, a.name)} aria-label={`Excluir ${a.name}`} className="h-7 gap-1 text-[11px] hover:text-destructive hover:border-destructive/40">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
